import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import ProfilePicture from '../profile-picture'
import Stars from '../stars'
import FormattedText from '../formatted-text'

import { useAuth } from '../../../data/hooks/auth'
import { Review } from '../../../data/book/data-types'
import { changeFollowStatus } from '../../../data/profile/folllow'
import Button from '../button'

import { formatDate } from '../../../utilities/date'
import { shortenString } from '../../../utilities/text'
import { up } from 'styled-breakpoints'
import { DialogLevel } from '../dialog'
import { useDialog } from '../dialog/hook'
import BookDialog from '../custom-dialogs/book'
import ColoredDialogBackdrop from '../custom-dialogs/colored-dialog-backdrop'

type ReviewProps = {
  review: Review
  focus: 'author' | 'book'
}

const ReviewItem: React.FunctionComponent<ReviewProps> = ({ review, focus }) => {
    const { t } = useTranslation("book")
    const { token } = useAuth()

    const [following, setFollowing] = useState(review.user.followed_by_me)

    const [full, setFull] = useState(false)
    const [needsShortening, body] = shortenString(review.body, full, 400)

    const { setComponent } = useDialog()

    return (
        <Container focus={focus}>
            { focus === 'author' && 
            <>
                <Author>
                    <ProfilePicture pictureUrl={review.user.picture} publicId={review.user.public_id} />
                    <Misc>
                        <UserName> {`${review.user.first_name} ${review.user.last_name}`} </UserName>
                        <Stats>
                            <StatValue>{review.user_stats.reviews_count}</StatValue> reivews 
                            <StatValue> · </StatValue>
                            <StatValue>{review.user_stats.followers_count}</StatValue> followers
                        </Stats>
                    </Misc>
                    {
                        token?.access!
                            ? <FollowButton 
                                following={following} 
                                onClick={async () => {
                                    setFollowing(!following)
                                    await changeFollowStatus(review.user.public_id, following, token?.access!)
                                }
                            }>
                                { `${following ? 'Unfollow' : 'Follow'}` }
                            </FollowButton>
                            : null
                    }
                </Author>
                <Header>
                    <Stars value={review.rating}/>
                    <DateTime focus={focus}>{formatDate(new Date(review.creation_date))}</DateTime>
                </Header>
            </>
            }
            {
                focus === 'book' &&
                <>
                    <BookImageContainer 
                        onClick={() => setComponent(<BookDialog book={review.book} needsUpdate={true} />, <ColoredDialogBackdrop bg={review.book.bg_color}/>, DialogLevel.Top)}
                    >
                        <BookImage src={review.book.image} alt={review.book.title} />
                        <SmallableStars value={review.rating}/>
                    </BookImageContainer>
                </>
            }
            <ReviewMainBody>
                <StatValue>{review.title}</StatValue>
                <div>
                    <FormattedText text={ review.is_spoiler ? `||${body}||` : body}/>
                    {needsShortening && <Switcher onClick={() => setFull(!full)}> { full ? t('read_less') : t('read_more') } </Switcher>}
                </div>
            </ReviewMainBody>
            {
                focus === 'book' && <DateTime focus={focus}>{formatDate(new Date(review.creation_date))}</DateTime>
            }
        </Container>
    )
}

const Container = styled.div<{ focus: 'author' | 'book' }>`
  display: grid;
  align-items: flex-start;

  grid-template-columns: ${ p => p.focus === 'author' ? '1fr' : 'auto 1fr' };
  grid-template-rows: ${ p => p.focus === 'author' ? 'auto auto 1fr' : 'auto 1fr' };

  grid-column-gap: 24px;
  grid-row-gap: ${ p=> p.focus === 'author' ? '24px' : '10px' };

  width: 100%;

  ${up('tablet')} {
    grid-template-columns: ${ p => p.focus === 'author' ? '1fr' : 'auto 1fr auto' };
  }
`

const BookImageContainer = styled.div`
    display: grid;
    align-items: center;
    justify-items: center;
    grid-gap: 10px;

    align-self: flex-start;
    grid-column: 1 / 2;
    grid-row: 1 / 2;

    cursor: pointer;
`

const SmallableStars = styled(Stars)`
    height: 15px;
`

const BookImage = styled.img`
    width: 65px;
    aspect-ratio: 6 / 9;
    border-radius: 10px;

    ${up('tablet')} {
        width: 100px;
    }
`

const Author = styled.div`
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-gap: 10px;
`

const Misc = styled.div`    
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 8px;
    align-items: center;
`

const UserName = styled.div`
    font-weight: bold;
    font-size: 16px;
`

const Stats = styled.div`
    color: ${p => p.theme.colors.darkGray};
    font-size: 14px;
`

const StatValue = styled.span`
    font-weight: bold;
`

const Header = styled.div`
    display: grid;
    align-items: center;
    grid-template-columns: 1fr auto;

    align-self: flex-start;
`

const DateTime = styled.div<{ focus: string }>`
    grid-column: ${ p => p.focus === 'author' ? 'default' : 1 };
    justify-self: center;

    font-size: 12px;
    color: ${p => p.theme.colors.darkGray};

    ${up('tablet')} {
        grid-column: default;
        font-size: 14px;
    }
`

const Switcher = styled.button`
    justify-self: start;

    padding: 0;
    margin-top: -10px;

    font-size: 14px;
    line-height: 24px;
    color: ${p => p.theme.colors.accent};

    border: none;
    background: none;

    cursor: pointer;
`

const FollowButton = styled(Button)<{ following: boolean }>`
    height: fit-content;

    margin-top: 0;

    background: ${p => p.following ? p.theme.colors.bg : p.theme.colors.accent};
    color: ${p => p.following ? p.theme.colors.accent : p.theme.colors.bg};
    
    border-color: ${p => p.following ? p.theme.colors.accent : p.theme.colors.bg};
    border-width: 1px;
    border-style: solid;
`

const ReviewMainBody = styled.div`
    display: grid;
    grid-gap: 10px;

    grid-row: span 2;

    word-wrap: break-word;
    overflow-wrap: break-word; 

    ${up('tablet')} {
        grid-row: default;
    }
`

export default ReviewItem